/**
 * Chargement du contenu (ADR 0003 §4, ADR 0004 §5) : fichiers réels (`content/*.json`, `data/processed/baseline_2026.json`)
 * copiés dans `dist/` au build. La baseline réelle, même `partial`, est fusionnée par-dessus la baseline de secours
 * (`content/fixtures/baseline_proto.json`, chiffres `source: "TODO"`) par `engine/src/baseline-merge.ts` : chaque chiffre
 * réel remplace le placeholder, les champs manquants gardent leur source TODO (l'UI affiche « non sourcé »).
 * Les fixtures de `ui/src/dev/` ne servent plus qu'au mock (`?mock=1`) ou si un fichier manque.
 */
import { mergeBaseline } from "../../engine/src/baseline-merge.js";
import type { Baseline, Figure, GameEvent, ParamSet, ScorecardDefs } from "../../engine/src/types.js";
import type { Content, ContentOrigin, FinancingLever, UiMeasure, UiPreset } from "./content-types.js";
import { parseFactcheckStatus } from "./factcheck.js";
import { parsePoliticalNames } from "./neutrality.js";
import { parseParamSources, parseSensitivity } from "./params-info.js";
import { FIXTURE_BASELINE, FIXTURE_EVENTS, FIXTURE_LEVERS, FIXTURE_MEASURES, FIXTURE_PARAMS } from "./dev/fixtures.js";

async function tryFetch<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(path, { cache: "no-cache" });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

interface Manifest {
  content: string[];
  data: string[];
}

/**
 * Curseurs de financement dérivés de `content/params.json` (source de vérité des bornes, ADR 0004 §4) :
 * un levier par paramètre `lev_<id>_max_bn`. Libellé = partie du label avant « : » (sans « Levier »).
 */
export function leversFromParams(params: ParamSet): FinancingLever[] {
  const out: FinancingLever[] = [];
  for (const [key, p] of Object.entries(params)) {
    const m = /^lev_([a-z0-9]+)_max_bn$/.exec(key);
    if (!m || p.value === null) continue;
    const id = m[1]!;
    const label = p.label.split(" : ")[0]!.replace(/^Levier\s+/i, "");
    const fig = (suffix: string): Figure | undefined => params[`lev_${id}_${suffix}`];
    const min = fig("min_bn");
    const leak = fig("leak_per_bn");
    const pol = fig("political_cost_per_bn");
    const todo = (f: Figure | undefined): Figure => f ?? { value: null, unit: "ratio", source: "TODO" };
    const hints: string[] = [];
    if (leak?.value != null) hints.push(`fuite / évitement ${(leak.value * 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} % par Md€`);
    if (fig("poverty_per_bn")?.value) hints.push("pèse sur la pauvreté");
    if (fig("co2_per_bn")?.value) hints.push("fait baisser les émissions");
    if (pol?.value != null && pol.value < 0) hints.push("populaire");
    out.push({
      id,
      label,
      hint: hints.join(" · "),
      min: min ?? { value: 0, unit: "bn_eur", source: p.source },
      max: p,
      step: 1,
      leak: todo(leak),
      political_cost_per_bn: todo(pol),
      sources: p.source === "TODO" ? [] : [p.source],
      status: p.source === "TODO" ? "non_chiffré" : "sourced",
    });
  }
  return out;
}

/** Contenu de développement du prototype (cohérent avec le mock `?mock=1`, chiffres placeholders `TODO`). */
export function fixtureContent(): Content {
  return {
    baseline: FIXTURE_BASELINE,
    params: FIXTURE_PARAMS,
    measures: FIXTURE_MEASURES,
    events: FIXTURE_EVENTS,
    levers: FIXTURE_LEVERS,
    unsourcedPaths: [],
    politicalNames: null,
    paramSources: null,
    sensitivity: null,
    presets: [],
    scorecard: null,
    factcheck: null,
    origin: { baseline: "fixture", params: "fixture", measures: "fixture", events: "fixture", levers: "fixture" },
  };
}

/** `content/scorecard.json` lu défensivement (forme validée en CI par le schéma zod) ; null si absent ou mal formé. */
export function parseScorecard(raw: unknown): ScorecardDefs | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Partial<ScorecardDefs>;
  if (typeof o.reference_preset_id !== "string" || !Array.isArray(o.indicators) || !o.indicators.length || !o.weights) return null;
  const w = o.weights;
  if (![w.min, w.max, w.step, w.default].every((x) => typeof x === "number" && Number.isFinite(x))) return null;
  return o as ScorecardDefs;
}

export async function loadContent(): Promise<Content> {
  // Le build écrit manifest.json avec la liste des fichiers présents : on ne demande que ceux-là.
  const manifest = (await tryFetch<Manifest>("manifest.json")) ?? { content: [], data: [] };
  const want = <T>(dir: "content" | "data", name: string): Promise<T | null> =>
    manifest[dir].includes(name) ? tryFetch<T>(`${dir}/${name}`) : Promise.resolve(null);
  const [baselineReal, baselineFixture, params, measures, events, names, paramSources, sensitivity, presets, fcStatus, scorecard] = await Promise.all([
    want<Baseline & { meta: { partial?: boolean } }>("data", "processed/baseline_2026.json"),
    want<Baseline>("content", "fixtures/baseline_proto.json"),
    want<ParamSet>("content", "params.json"),
    want<UiMeasure[]>("content", "measures.json"),
    want<GameEvent[]>("content", "events.json"),
    want<unknown>("content", "policy/political-names.json"),
    want<unknown>("content", "params.sources.json"),
    want<unknown>("content", "params.sensitivity.json"),
    want<UiPreset[]>("content", "presets.json"),
    want<unknown>("content", "factcheck.status.json"),
    want<unknown>("content", "scorecard.json"),
  ]);
  const origin: Record<string, ContentOrigin> = {};
  const fallback = baselineFixture ?? FIXTURE_BASELINE;
  let baseline: Baseline;
  let unsourcedPaths: string[] = [];
  if (baselineReal && !baselineReal.meta.partial) {
    baseline = baselineReal;
    origin.baseline = "file";
  } else if (baselineReal) {
    const merged = mergeBaseline(fallback, baselineReal);
    baseline = merged.baseline;
    unsourcedPaths = merged.fallback_paths;
    origin.baseline = "merged";
  } else {
    baseline = fallback;
    origin.baseline = "fixture";
  }
  let paramSet: ParamSet;
  if (params && Object.keys(params).length) {
    paramSet = params;
    origin.params = "file";
  } else {
    paramSet = FIXTURE_PARAMS;
    origin.params = "fixture";
  }
  const pick = <T>(real: T[] | null, fx: T[], key: string): T[] => {
    if (real && real.length) {
      origin[key] = "file";
      return real;
    }
    origin[key] = "fixture";
    return fx;
  };
  const levers = origin.params === "file" ? leversFromParams(paramSet) : FIXTURE_LEVERS;
  origin.levers = origin.params === "file" ? "file" : "fixture";
  return {
    baseline,
    params: paramSet,
    measures: pick(measures, FIXTURE_MEASURES, "measures"),
    events: pick(events, FIXTURE_EVENTS, "events"),
    levers,
    unsourcedPaths,
    politicalNames: parsePoliticalNames(names),
    paramSources: parseParamSources(paramSources),
    sensitivity: parseSensitivity(sensitivity),
    presets: Array.isArray(presets) ? presets : [],
    factcheck: parseFactcheckStatus(fcStatus),
    scorecard: parseScorecard(scorecard),
    origin: origin as Content["origin"],
  };
}
